<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import KanbanColumn from './KanbanColumn.vue';
import KanbanListView from './KanbanListView.vue';
import KanbanCalendarView from './KanbanCalendarView.vue';
import KanbanHeader from './KanbanHeader.vue';
import KanbanBulkActions from './KanbanBulkActions.vue';
import LossReasonModal from './LossReasonModal.vue';
import WonValueModal from './WonValueModal.vue';
import KanbanOpportunityModal from './KanbanOpportunityModal.vue';
import KanbanSettingsModal from './KanbanSettingsModal.vue';
import KanbanNewPipelineModal from './KanbanNewPipelineModal.vue';
import ColumnDescriptionPopover from './components/automations/ColumnDescriptionPopover.vue';
import ColumnAutomationsPanel from './components/automations/ColumnAutomationsPanel.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const DEFAULT_FILTERS = { status: ['open', 'waiting', 'won', 'lost'] };
const pendingMove = ref(null);
const pendingWon = ref(null);
const pendingWaiting = ref(null);
const activeFilters = ref({ ...DEFAULT_FILTERS });
const currentUserId = computed(() => store.getters.getCurrentUser?.id);
const viewMode = ref('board');
const selectedCardId = ref(null);
const selectedCard = computed(() =>
  selectedCardId.value
    ? (store.state.kanban.cards.find(c => c.id === selectedCardId.value) ??
      null)
    : null
);
const selectedCardInitialTab = ref('main');
const showSettings = ref(false);
const showNewPipeline = ref(false);
const descriptionColumn = ref(null);
const automationsColumn = ref(null);
const showCreateCardModal = ref(false);
const createCardVirtualCard = ref(null);

// ── Bulk selection ─────────────────────────────────────────────────
const selectionMode = ref(false);
const selectedIds = ref([]);
const showBulkDropdown = ref(false);
const bulkDropdownPos = ref({ top: 0, right: 0 });
const bulkContainerRef = ref(null);

const activePipeline = computed(() => store.getters['kanban/activePipeline']);

function viewModeKey(pipelineId) {
  return `kanban_view_mode_pref_${pipelineId}_${currentUserId.value}`;
}

watch(
  activePipeline,
  pipeline => {
    if (!pipeline || !currentUserId.value) return;
    try {
      const raw = localStorage.getItem(viewModeKey(pipeline.id));
      viewMode.value = raw ? JSON.parse(raw) : 'board';
    } catch {
      viewMode.value = 'board';
    }
  },
  { immediate: true }
);

watch(viewMode, val => {
  const pipeline = activePipeline.value;
  if (!pipeline || !currentUserId.value) return;
  try {
    localStorage.setItem(viewModeKey(pipeline.id), JSON.stringify(val));
  } catch {
    // ignora falha de persistência
  }
});

const activePipelineColumns = computed(
  () => activePipeline.value?.columns ?? []
);
const cardsByColumn = columnId =>
  store.getters['kanban/cardsByColumn'](columnId);

const isPostSales = computed(
  () => activePipeline.value?.category === 'post_sales'
);
const lossReasons = computed(() =>
  isPostSales.value
    ? (activePipeline.value?.cancel_reasons ?? [])
    : (activePipeline.value?.loss_reasons ?? [])
);
const waitingReasons = computed(
  () => activePipeline.value?.waiting_reasons ?? []
);

function onToggleSelection() {
  selectionMode.value = !selectionMode.value;
  selectedIds.value = [];
  showBulkDropdown.value = false;
}

function onCardSelected(id) {
  const idx = selectedIds.value.indexOf(id);
  if (idx === -1) selectedIds.value = [...selectedIds.value, id];
  else selectedIds.value = selectedIds.value.filter(i => i !== id);
}

function onOpenBulk(btnEl) {
  if (!btnEl) return;
  const rect = btnEl.getBoundingClientRect();
  bulkDropdownPos.value = {
    top: rect.bottom + 8,
    right: window.innerWidth - rect.right,
  };
  showBulkDropdown.value = !showBulkDropdown.value;
}

function onBulkDone() {
  selectionMode.value = false;
  selectedIds.value = [];
  showBulkDropdown.value = false;
  store.dispatch('kanban/fetchCards', activeFilters.value);
}

function onDocMousedown(e) {
  if (!showBulkDropdown.value) return;
  const container = bulkContainerRef.value;
  if (e.target.closest('[data-bulk-dropdown]')) return;
  if (container && !container.contains(e.target)) {
    showBulkDropdown.value = false;
  }
}

function onOpenCardById(cardId) {
  const card = store.state.kanban.cards.find(c => c.id === cardId);
  if (card) selectedCardId.value = card.id;
}

onMounted(async () => {
  await store.dispatch('kanban/fetchPipelines');
  await store.dispatch('kanban/fetchCards', {
    ...DEFAULT_FILTERS,
    ...activeFilters.value,
  });
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  document.addEventListener('mousedown', onDocMousedown);
  const openCardId = route.query?.openCard
    ? Number(route.query.openCard)
    : null;
  if (openCardId) onOpenCardById(openCardId);
});

onUnmounted(() => document.removeEventListener('mousedown', onDocMousedown));

function onFilterChange(filters) {
  activeFilters.value = { ...DEFAULT_FILTERS, ...filters };
  store.dispatch('kanban/fetchCards', activeFilters.value);
}

function onPipelineChange() {
  store.dispatch('kanban/fetchCards', {
    ...DEFAULT_FILTERS,
    ...activeFilters.value,
  });
}

async function executeMove(
  { cardId, columnId, position, afterCardId },
  reason = null
) {
  const reasonPayload = isPostSales.value
    ? { cancelReason: reason }
    : { lossReason: reason };
  const result = await store.dispatch('kanban/moveCard', {
    cardId,
    columnId,
    position,
    afterCardId,
    ...reasonPayload,
  });
  if (!result.success) {
    store.dispatch('kanban/fetchCards', activeFilters.value);
  }
}

// Soltar numa etapa Ganho/Perdido/Aguardando muda o status do card, então passa
// pelo mesmo pedido de motivo/valor do menu de status antes de mover.
function onCardMoved(moveData) {
  const column = activePipelineColumns.value.find(
    c => c.id === moveData.columnId
  );
  const card = store.state.kanban.cards.find(c => c.id === moveData.cardId);
  const targetType = column?.column_type;

  if (!card || card.status === targetType || targetType === 'default') {
    executeMove(moveData);
    return;
  }

  const drag = { ...moveData, fromDrag: true };
  if (targetType === 'lost') {
    pendingMove.value = { ...drag, columnType: 'lost', anchor: null };
  } else if (targetType === 'won') {
    pendingWon.value = { ...card, _anchor: null, drag };
  } else if (targetType === 'waiting') {
    pendingWaiting.value = { cardId: card.id, anchor: null, drag };
  } else {
    executeMove(moveData);
  }
}

// cancelar um arraste deixa o card onde o board o soltou; recarrega para voltar
function revertDrag(pending) {
  if (pending?.fromDrag || pending?.drag) {
    store.dispatch('kanban/fetchCards', activeFilters.value);
  }
}

async function addLossReason(reason) {
  const pipeline = activePipeline.value;
  if (!pipeline) return;
  if (isPostSales.value) {
    const updated = [...new Set([...pipeline.cancel_reasons, reason])];
    await store.dispatch('kanban/updatePipeline', {
      id: pipeline.id,
      cancel_reasons: updated,
    });
  } else {
    const updated = [...new Set([...pipeline.loss_reasons, reason])];
    await store.dispatch('kanban/updatePipeline', {
      id: pipeline.id,
      loss_reasons: updated,
    });
  }
}

async function addWaitingReason(reason) {
  const pipeline = activePipeline.value;
  if (!pipeline) return;
  const updated = [...new Set([...(pipeline.waiting_reasons ?? []), reason])];
  await store.dispatch('kanban/updatePipeline', {
    id: pipeline.id,
    waiting_reasons: updated,
  });
}

async function confirmMove(reason) {
  const move = pendingMove.value;
  const hasLostColumn = activePipelineColumns.value.some(
    c => c.column_type === 'lost'
  );
  const reasonField = isPostSales.value ? 'cancel_reason' : 'loss_reason';

  if (!hasLostColumn) {
    await store.dispatch('kanban/updateCard', {
      cardId: move.cardId,
      data: { status: 'lost', [reasonField]: reason },
    });
  } else {
    await executeMove(move, reason);
  }
  pendingMove.value = null;
}

function cancelMove() {
  revertDrag(pendingMove.value);
  pendingMove.value = null;
}

// sem column_id o backend leva o card para a etapa Aguardando, se o funil tiver
async function confirmWaiting(reason) {
  const move = pendingWaiting.value;
  pendingWaiting.value = null;
  const data = { status: 'waiting', waiting_reason: reason };
  if (move.drag) {
    data.column_id = move.drag.columnId;
    data.after_card_id = move.drag.afterCardId;
  }
  await store.dispatch('kanban/updateCard', { cardId: move.cardId, data });
  store.dispatch('kanban/fetchCards', activeFilters.value);
}

function cancelWaiting() {
  revertDrag(pendingWaiting.value);
  pendingWaiting.value = null;
}

async function confirmWon(value) {
  const card = pendingWon.value;
  const wonColumn = activePipelineColumns.value.find(
    c => c.column_type === 'won'
  );
  pendingWon.value = null;

  const today = new Date().toISOString().slice(0, 10);
  const payload = { status: 'won', closing_date: today, position: 0 };
  if (value != null) payload.value = value;
  if (wonColumn) payload.column_id = wonColumn.id;
  if (card.drag) {
    delete payload.position;
    payload.column_id = card.drag.columnId;
    payload.after_card_id = card.drag.afterCardId;
  }

  await store.dispatch('kanban/updateCard', { cardId: card.id, data: payload });
  store.dispatch('kanban/fetchCards', activeFilters.value);
}

function cancelWon() {
  revertDrag(pendingWon.value);
  pendingWon.value = null;
}

function onChangeStatus({ card, targetStatus, anchor }) {
  const columns = activePipelineColumns.value;

  if (targetStatus === 'lost') {
    const lostColumn = columns.find(c => c.column_type === 'lost');
    pendingMove.value = {
      cardId: card.id,
      columnId: lostColumn?.id ?? card.column_id,
      position: card.position,
      columnType: 'lost',
      anchor: anchor ?? null,
    };
    return;
  }

  if (targetStatus === 'won') {
    pendingWon.value = { ...card, _anchor: anchor ?? null };
    return;
  }

  if (targetStatus === 'waiting') {
    pendingWaiting.value = { cardId: card.id, anchor: anchor ?? null };
    return;
  }

  store
    .dispatch('kanban/updateCard', {
      cardId: card.id,
      data: { status: 'open', column_id: card.column_id, position: 0 },
    })
    .then(() => {
      store.dispatch('kanban/fetchCards', activeFilters.value);
    });
}

function onOpenContact(card) {
  const accountId = store.getters.getCurrentAccount?.id;
  if (card.contact?.id && accountId) {
    window.open(
      `/app/accounts/${accountId}/contacts/${card.contact.id}`,
      '_blank'
    );
  }
}

function onOpenConversation(card) {
  const accountId = store.getters.getCurrentAccount?.id;
  const conversationId = card.conversation?.id ?? card.conversation_id;
  if (conversationId && accountId) {
    router.push(`/app/accounts/${accountId}/conversations/${conversationId}`);
  }
}

function onCardClick(card) {
  selectedCardInitialTab.value = 'main';
  selectedCardId.value = card.id;
}

function onCardOpenTasks(card) {
  selectedCardInitialTab.value = 'tasks';
  selectedCardId.value = card.id;
}

function onOpenColumnDescription({ column, anchor }) {
  descriptionColumn.value = { column, anchor };
}

function onOpenColumnAutomations(column) {
  automationsColumn.value = column;
}

function onOpenCreateCard(column) {
  const pipeline = activePipeline.value;
  if (!pipeline) return;

  createCardVirtualCard.value = {
    pipeline_id: pipeline.id,
    column_id: column.id,
    columns: pipeline.columns ?? [],
    title: '',
    contact: null,
    status: 'open',
    value: null,
    custom_attributes: {},
  };

  showCreateCardModal.value = true;
}

function onCreateCardWithDate({ columnId, closingDate }) {
  const pipeline = activePipeline.value;
  if (!pipeline) return;

  createCardVirtualCard.value = {
    pipeline_id: pipeline.id,
    column_id: columnId,
    columns: pipeline.columns ?? [],
    title: '',
    contact: null,
    status: 'open',
    value: null,
    closing_date: closingDate,
    custom_attributes: {},
  };

  showCreateCardModal.value = true;
}

function onCreateCardModalClose() {
  showCreateCardModal.value = false;
  createCardVirtualCard.value = null;
  store.dispatch('kanban/fetchCards');
}

async function saveColumnDescription(description) {
  const pipeline = activePipeline.value;
  const target = descriptionColumn.value?.column;
  if (!pipeline || !target) return;

  const columns = activePipelineColumns.value.map(col =>
    col.id === target.id ? { ...col, description } : { ...col }
  );
  await store.dispatch('kanban/updatePipeline', { id: pipeline.id, columns });
  descriptionColumn.value = null;
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <KanbanHeader
      :selection-mode="selectionMode"
      :selected-count="selectedIds.length"
      :view-mode="viewMode"
      @filter-change="onFilterChange"
      @pipeline-change="onPipelineChange"
      @open-settings="showSettings = true"
      @open-new-pipeline="showNewPipeline = true"
      @toggle-selection="onToggleSelection"
      @open-bulk="onOpenBulk"
      @view-mode-change="viewMode = $event"
    />

    <div
      v-if="viewMode === 'board'"
      class="flex gap-4 p-4 overflow-x-auto overflow-y-hidden flex-1 min-h-0 bg-n-background items-stretch"
    >
      <KanbanColumn
        v-for="col in activePipelineColumns"
        :key="col.id"
        :column="col"
        :cards="cardsByColumn(col.id)"
        :selection-mode="selectionMode"
        :selected-ids="selectedIds"
        :pending-lost-card-id="pendingMove?.cardId ?? null"
        :pending-won-card-id="pendingWon?.id ?? null"
        @card-moved="onCardMoved"
        @change-status="onChangeStatus"
        @open-contact="onOpenContact"
        @open-conversation="onOpenConversation"
        @card-click="onCardClick"
        @open-tasks="onCardOpenTasks"
        @card-selected="onCardSelected"
        @open-description="onOpenColumnDescription"
        @open-automations="onOpenColumnAutomations"
        @open-create-card="onOpenCreateCard"
      />
    </div>
    <KanbanListView
      v-else-if="viewMode === 'list' && activePipeline"
      :key="activePipeline.id"
      :pipeline="activePipeline"
      :cards-by-column="cardsByColumn"
      @card-click="onCardClick"
    />
    <KanbanCalendarView
      v-else-if="viewMode === 'calendar' && activePipeline"
      :key="activePipeline.id"
      :pipeline="activePipeline"
      :cards-by-column="cardsByColumn"
      @card-click="onCardClick"
      @create-card="onCreateCardWithDate"
    />

    <LossReasonModal
      v-if="pendingMove && pendingMove.columnType === 'lost'"
      :reasons="lossReasons"
      :anchor="pendingMove.anchor"
      @confirm="confirmMove"
      @cancel="cancelMove"
      @create="addLossReason"
    />

    <LossReasonModal
      v-if="pendingWaiting"
      :reasons="waitingReasons"
      :anchor="pendingWaiting.anchor"
      color="amber"
      @confirm="confirmWaiting"
      @cancel="cancelWaiting"
      @create="addWaitingReason"
    />

    <WonValueModal
      v-if="pendingWon"
      :initial-value="pendingWon.value"
      :anchor="pendingWon._anchor"
      @confirm="confirmWon"
      @cancel="cancelWon"
    />

    <KanbanOpportunityModal
      v-if="selectedCard"
      :card="selectedCard"
      :initial-tab="selectedCardInitialTab"
      @close="selectedCardId = null"
      @saved="selectedCardId = null"
      @deleted="selectedCardId = null"
    />

    <KanbanOpportunityModal
      v-if="showCreateCardModal && createCardVirtualCard"
      :card="createCardVirtualCard"
      is-new
      @close="onCreateCardModalClose"
      @deleted="onCreateCardModalClose"
    />

    <KanbanSettingsModal v-if="showSettings" @close="showSettings = false" />
    <KanbanNewPipelineModal
      v-if="showNewPipeline"
      @close="showNewPipeline = false"
      @created="showNewPipeline = false"
    />

    <ColumnDescriptionPopover
      v-if="descriptionColumn"
      :column="descriptionColumn.column"
      :anchor="descriptionColumn.anchor"
      @save="saveColumnDescription"
      @cancel="descriptionColumn = null"
    />

    <ColumnAutomationsPanel
      v-if="automationsColumn"
      :pipeline-id="activePipeline?.id"
      :column="automationsColumn"
      @close="automationsColumn = null"
    />
  </div>

  <Teleport to="body">
    <div
      v-if="showBulkDropdown"
      ref="bulkContainerRef"
      class="fixed z-50"
      :style="{
        top: bulkDropdownPos.top + 'px',
        right: bulkDropdownPos.right + 'px',
      }"
    >
      <KanbanBulkActions
        :selected-ids="selectedIds"
        :columns="activePipelineColumns"
        @done="onBulkDone"
        @cancel="showBulkDropdown = false"
        @close="showBulkDropdown = false"
      />
    </div>
  </Teleport>
</template>
